import { Component, EventEmitter, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';
import { forkJoin } from 'rxjs';
import { DocumentService } from 'src/app/documents/documents.service';
import { OrganisationService } from 'src/app/organisation/organisation.service';
import { Organisation } from 'src/app/generated/services/organisations/organisation';
import { CrewChangeServices, CredentialItemsReversMap } from 'src/app/transportation/vessels/crew-change/crew-change.service';
import { CrewProfileToolbarComponent } from './crew-profile-toolbar/crew-profile-toolbar.component';
import { PgToolbarComponent } from 'src/app/shared/components/title-bar/title-bar.component';
import { saveAs } from 'file-saver';
import { SharedModule } from 'src/app/shared/shared.module';
import { MatCard, MatCardContent, MatCardHeader, MatCardModule } from '@angular/material/card';
import { MaterialImageViewerComponent } from 'src/app/shared/components/material-image-viewer/material-image-viewer';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { DateTimeHelpers } from 'src/app/shared/helpers/date-time.helpers';
import { TimelineComponent, TimelineItem } from 'src/app/shared/components/timeline/timeline.component';
import { MaterialPdfViewerPopUpComponent } from 'src/app/shared/components/material-pdf-viewer-pop-up/material-pdf-viewer-pop-up.component';
import { CrewServiceSpa } from 'src/app/generated/services/transportation/crew/crewSpa';
import { Crew, CrewAppraisal, CrewChange } from 'src/app/generated/services/transportation/crew/crew';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-crew-profile',
  templateUrl: './crew-profile.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SharedModule,
    RouterModule,
    MaterialImageViewerComponent,
    TimelineComponent,
    MatCard,
    MatCardContent,
    MatListModule,
    MatDividerModule,
    MatButtonModule,
    MatTableModule,
    MatTooltipModule,
    MatIconModule,
    MatPaginatorModule,
    MatSortModule,
    MaterialPdfViewerPopUpComponent, MatCardHeader, MatCardModule],
})
export class CrewProfileComponent implements OnInit, PgToolbarComponent {

  title = new EventEmitter<string>();
  toolbarComponentType = CrewProfileToolbarComponent;
  toolbarComponent!: CrewProfileToolbarComponent;
  crew!: Crew;
  message!: string;
  rankItem: string[][] = [];
  crewId: string = '';
  nationality!: Organisation;
  _organisationsMap!: Map<string, Organisation>;
  rate: any[] = [];
  appraisals: CrewAppraisal[] = [];
  showPassport!: boolean;
  showCv!: boolean;
  showSeaman!: boolean;
  crewChangeTimeline: TimelineItem[] = [];
  previewFileId!: string;
  filePreviewTitle!: string;
  showDocs: boolean[] = [];
  showRemarkDoc: boolean[] = [];
  authors: Crew[] = [];
  showAppraisal!: boolean;
  appraisal!: CrewAppraisal;
  filePreviewDialogModal: any | undefined;
  assessorNames = new Map<string, string>();

  constructor(
    private _crewChangeService: CrewChangeServices,
    private _activateRoute: ActivatedRoute,
    private _organisationService: OrganisationService,
    private _documentService: DocumentService,
    private _crewService: CrewServiceSpa
  ) { }

  ngOnInit(): void {
    this.title.emit('Crew');
    this._activateRoute.paramMap.subscribe(p => {
      this.crewId = p.get('id') ?? '';
      forkJoin([
        this._organisationService.getOrganisationIdToOrganisationMap(),
      ]).subscribe((result) => {
        this._organisationsMap = result[0];
        this.initCrew();
      });
    });
  }

  getOrganisation(id: string | undefined) {
    if (id) {
      return this._organisationsMap.get(id);
    }
  }

  getCredentialsLabel(value: string) {
    return CredentialItemsReversMap.get(value);
  }

  initCrew() {
    this._crewService.getCrewById({ id: this.crewId }).then(x => {
      this.crew = x.data!;
      this.appraisals = this.crew.crewChanges.filter(c => c.appraisals && c.appraisals.length > 0).flatMap(c => c.appraisals);
      this.appraisal = this.appraisals[0];
      this.rate = this.calculateRating(this.appraisals);
      const assessorIds = [
        ...new Set(
          this.appraisals
            .map(appraisal => appraisal.assessorId)
            .filter(id => !!id)
        )
      ];
      assessorIds.forEach(id => {
        this._crewService.getCrewById({ id }).then(result => {
          const assessor = result.data;
          if (assessor) {
            this.assessorNames.set(
              id,
              `${assessor.firstName} ${assessor.lastName}`
            );
          }
        });
      });
      this.crew.crewChanges = this.crew.crewChanges.filter(crewChange => crewChange.isDeleted === false);
      this.toolbarComponent.crew = this.crew;
      this.nationality = this._organisationsMap.get(this.crew.nationality)!;
      this.calculateAge();
      this.crewChangeTimeline = this.convertCrewChangeToTimeline(this.crew.crewChanges);
      this.showDocs = (this.crew.credentials) ? this.crew.credentials.map(() => false) : [];
      this.showRemarkDoc = (this.crew.remarks) ? this.crew.remarks.map(() => false) : [];
    });
  }

  nationalityFormatter(data: Organisation) {
    return data.nameTranslations['en'];
  }

  calculateAge() {
    return moment().diff(moment(this.crew.birthDate), 'year');
  }

  getStatus() {
    return this._crewChangeService.getCrewStatus(this.crew);
  }

  planTime(crewChange: CrewChange, selectedPlan: string) {
    if (this._organisationsMap) {
      return this._crewChangeService.planTime(crewChange, selectedPlan, this._organisationsMap);
    }
  }

  planPlace(crewChange: CrewChange, selectedPlan: string) {
    if (this._organisationsMap) {
      return this._crewChangeService.planPlace(crewChange, selectedPlan, this._organisationsMap);
    }
  }

  onboardTimeMonthsDays(crewChange: CrewChange) {
    return this._crewChangeService.onboardTimeMonthsDays(crewChange);
  }

  travelTimeDays(crewChange: CrewChange) {
    return this._crewChangeService.travelTimeDays(crewChange);
  }

  getLastCrewChange(crewChanges: CrewChange[]) {
    if (crewChanges.length > 0) {
      return this._crewChangeService.getLastCrewChange(crewChanges);
    } else {
      return null;
    }
  }

  downLoadDocument(id: string) {
    this._documentService.download(id).subscribe(f => {
      saveAs(f.body, `${id}.pdf`);
    });
  }

  convertCrewChangeToTimeline(crewChanges: CrewChange[]) {
    const sorted = crewChanges
      .flatMap(crewChange => crewChange.item
        .sort((i1, i2) => i2.time - i1.time)
        .map(i => (
          {
            plan: i.plan,
            place: i.place,
            time: i.time,
            vesselName: crewChange.vesselName,
            rank: crewChange.rank,
          }
        )))
      .sort((i1, i2) => i2.time - i1.time);
    const t = sorted.map((plan, index) => {
      const timeline: TimelineItem = {
        title: plan.plan,
        description: this.description(plan, sorted[index + 1]),
        date: moment(plan.time).format('YYYY-MM-DD'),
        iconStyles: (plan.plan === 'Off Board') ? 'bg-warning fa fa-ship' :
          (plan.plan === 'Outgoing Flight') ? 'bg-warning fas fa-plane-departure' :
            (plan.plan === 'Incoming Flight') ? 'bg-success fas fa-plane-arrival' :
              (plan.plan === 'On Board') ? 'bg-success fa fa-ship' : ''
      };
      return timeline;
    });
    return t;
  }

  findDiff(plan:any, before:any, timeUnit:string) {
    if (before) {
      if (timeUnit === 'months') {
        return `${Math.round(+ moment(plan.time).diff(moment(before.time), 'months', true).toFixed(0))} months
        (${moment(plan.time).diff(moment(before.time), 'days', true).toFixed(0)} days)`;
      } else if (timeUnit === 'days') {
        return `${moment(plan.time).diff(moment(before.time), 'days', true).toFixed(0)} days
        (${moment(plan.time).diff(moment(before.time), 'hours', true).toFixed(0)} hours)`;
      }
    } else {
      return '';
    }
  }

  description(plan: any, before: any): string {
    if (plan.plan === this._crewChangeService.getPlanningPhases()[0] && before) {
      return `${this._organisationsMap.get(plan.place)!.nameTranslations['en']} (Rest: ${this.findDiff(plan, before, 'months')})`;
    } else if (plan.plan === this._crewChangeService.getPlanningPhases()[0] && !before) {
      return `${this._organisationsMap.get(plan.place)!.nameTranslations['en']}`;
    } else if (plan.plan === this._crewChangeService.getPlanningPhases()[1]) {
      return `${plan.vesselName} in
      ${this._organisationsMap.get(plan.place)!.nameTranslations['en']} (Travel: ${this.findDiff(plan, before, 'days')}) `;
    } else if (plan.plan === this._crewChangeService.getPlanningPhases()[2]) {
      return `${this._organisationsMap.get(plan.place)!.nameTranslations['en']} (On Board: ${this.findDiff(plan, before, 'months')})`;
    } else if (plan.plan === this._crewChangeService.getPlanningPhases()[3]) {
      return `${this._organisationsMap.get(plan.place)!.nameTranslations['en']} (Travel: ${this.findDiff(plan, before, 'days')})`;
    }
    return '';
  }

  calculateRating(crewAppraisals?: CrewAppraisal[]) {
    return this._crewChangeService.calculateRating(crewAppraisals ?? []);
  }

  getLatestContract(crewChange: CrewChange) {
    const extensionLength = (crewChange.contract) ? crewChange.contract.extensions.length : 0;
    return (crewChange.contract && extensionLength > 0) ? crewChange.contract.extensions[extensionLength - 1] :
      (crewChange.contract) ? crewChange.contract : null;
  }

  getLatestContractSalary(crewChange: CrewChange) {
    const latestContract = this.getLatestContract(crewChange);
    return (latestContract) ? (latestContract.basicSalaryUsd +
      latestContract.guaranteedOvertimeUsd +
      latestContract.leavePayUsd +
      latestContract.leaveSubsistenceUsd +
      latestContract.tankerAllowanceUsd) : 0;
  }

  getLatestContractExpDate(crewChange: CrewChange) {
    const latestContract = this.getLatestContract(crewChange);
    return (latestContract) ? moment(latestContract.date).add(latestContract.durationInMonths, 'month').format('YYYY-MM-DD') : '';
  }

  getCrewStatus() {
    return this._crewChangeService.getCrewActivationStatus(this.crew);
  }

  findCrew(id: string) {
    return this.assessorNames.get(id) ?? '';
  }
  showAppraisalPreview(app: CrewAppraisal) {
    this.appraisal = app;
  }

  closeAppraisalPreview() {
    this.filePreviewDialogModal.hide();
    this.showAppraisal = false;
  }

  formatDatestring(date: string) {
    return DateTimeHelpers.formatDateString(date);
  }
}
